import React, { useState } from "react";
import { FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { auth } from "../../firebase";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";


const commonPasswords = [
  "password", "123456", "123456789", "qwerty", "abc123", "password123",
  "admin", "letmein", "welcome", "monkey", "1234567890", "password1"
];

const UserChangePassword = ({ userData, isMandatory = false, onPasswordOtpRequired }) => {
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  
  const [passwordFields, setPasswordFields] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [passwordTouched, setPasswordTouched] = useState({
    new: false,
    confirm: false,
  });

  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [passwordPolicyError, setPasswordPolicyError] = useState("");

  const coreValidations = {
    length:
      passwordFields.new.length >= 8 &&
      passwordFields.new.length <= 24,
    uppercaseLowercase:
      /[A-Z]/.test(passwordFields.new) &&
      /[a-z]/.test(passwordFields.new),
    number: /\d/.test(passwordFields.new),
    special: /[!@#$%^&*]/.test(passwordFields.new),
  };

  const passwordStrength =
    [
      coreValidations.length,
      coreValidations.uppercaseLowercase,
      coreValidations.number,
      coreValidations.special
    ].filter(Boolean).length;

  const errorValidations = {
    noSpaces: !/\s/.test(passwordFields.new),
    noCommon: !commonPasswords.includes(passwordFields.new),
    noRepeats: !/(.)\1\1/.test(passwordFields.new),
    match:
      passwordFields.new === passwordFields.confirm &&
      passwordFields.confirm.length > 0,
    notUsername:
      !userData?.fullName ||
      passwordFields.new.toLowerCase() !== userData.fullName.toLowerCase(),
    notOldPassword: passwordFields.new !== passwordFields.current,
  };

  const allValid =
    passwordFields.current.trim().length > 0 &&
    Object.values(coreValidations).every(Boolean) &&
    Object.values(errorValidations).every(Boolean);

  const newPasswordHasError =
    passwordTouched.new &&
    passwordFields.new.length > 0 &&
    (!coreValidations.length ||
      !coreValidations.uppercaseLowercase ||
      !coreValidations.number ||
      !coreValidations.special ||
      !errorValidations.noSpaces ||
      !errorValidations.noCommon ||
      !errorValidations.noRepeats ||
      !errorValidations.notUsername ||
      !errorValidations.notOldPassword);

  const confirmPasswordHasError =
    passwordTouched.confirm &&
    passwordFields.confirm.length > 0 && !errorValidations.match;

  const handlePasswordCancel = () => {
    setPasswordFields({ current: "", new: "", confirm: "" });
    setPasswordTouched({ new: false, confirm: false });
    setCurrentPasswordError("");
    setPasswordPolicyError("");
  };

  const handlePasswordUpdate = async () => {
    if (!passwordFields.current.trim()) {
      setCurrentPasswordError("Current password is required.");
      return;
    }

    setIsPasswordSubmitting(true);
    setCurrentPasswordError("");
    setPasswordPolicyError("");

    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        setPasswordPolicyError("Session expired. Please log in again.");
        return;
      }

      const email = userData?.email || authUser.email || "";
      if (!email) {
        setPasswordPolicyError("Unable to determine your email address. Please log in again.");
        return;
      }

      const credential = EmailAuthProvider.credential(email, passwordFields.current);
      await reauthenticateWithCredential(authUser, credential);

      const pendingPasswordChange = {
        uid: authUser.uid,
        email,
        currentPassword: passwordFields.current,
        newPassword: passwordFields.new,
        timestamp: Date.now(),
        type: 'password-change',
      };
      localStorage.setItem('pendingPasswordChange', JSON.stringify(pendingPasswordChange));

      const { createStoredLoginOtpChallenge } = await import("../../services/authFlowService");
      const { sendOtpEmail } = await import("../../services/otpService");
      
      const otpChallenge = createStoredLoginOtpChallenge({
        email,
        role: 'user',
        roles: ['user'],
        route: 'password-change',
        keepSignedIn: false
      });

      console.log("✅ OTP code sent for password change verification");

      await sendOtpEmail({
        email: 'auravit2026@gmail.com',
        otp: otpChallenge.otp,
        purpose: 'password-change',
        requestId: otpChallenge.requestId,
        expiresAt: otpChallenge.expiresAt,
      });

      // Trigger parent to show OTP modal
      if (onPasswordOtpRequired) {
        onPasswordOtpRequired({
          email,
          newPassword: passwordFields.new,
        });
      }
    } catch (err) {
      console.error('Password verification failed:', err);
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setCurrentPasswordError("Incorrect Password");
      } else {
        setPasswordPolicyError(err?.message || "Password verification failed.");
      }
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const showNewPasswordErrors = passwordFields.new.length > 0;
  const showConfirmPasswordErrors = passwordFields.confirm.length > 0;

  return (
    <>
      <div className="psp-section-header" style={{ marginBottom: '20px' }}>
        <h2 className="psp-section-title">Change Password</h2>
      </div>

      <p style={{ 
        fontSize: '15px', 
        color: '#666', 
        margin: '0 0 20px 0',
        lineHeight: '1.4' 
      }}>
        Your new password must be different from your previous passwords
      </p>

      <div className="psp-field">
        <label>Current Password</label>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          <FiLock style={{
            position: 'absolute',
            left: '16px',
            color: '#666',
            zIndex: 1,
            fontSize: '20px'
          }} />
          <input
            type={showCurrentPassword ? "text" : "password"}
            maxLength={64}
            value={passwordFields.current}
            onChange={(e) => {
              setPasswordFields({ ...passwordFields, current: e.target.value });
              setCurrentPasswordError("");
            }}
            style={{
              padding: '14px 16px',
              paddingLeft: '52px',
              paddingRight: '52px',
              width: '100%',
              boxSizing: 'border-box',
              border: currentPasswordError ? '1px solid #e53e3e' : '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              fontSize: '15px',
              fontFamily: 'inherit',
              color: '#1e293b',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '16px',
              cursor: 'pointer',
              color: '#666',
              zIndex: 1,
              fontSize: '20px',
              transition: 'color 0.2s ease'
            }}
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
          >
            {showCurrentPassword ? <FiEye /> : <FiEyeOff />}
          </span>
        </div>
        {currentPasswordError && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            {currentPasswordError}
          </div>
        )}
      </div>

      <div className="psp-field">
        {passwordPolicyError && (
          <div style={{
            color: "#c53030",
            backgroundColor: "#fff5f5",
            padding: "10px",
            borderRadius: "5px",
            marginBottom: "15px",
            fontSize: "14px",
            border: "1px solid #fc8181"
          }}>
            ⚠️ {passwordPolicyError}
          </div>
        )}
        <label>New Password</label>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          <FiLock style={{
            position: 'absolute',
            left: '16px',
            color: '#666',
            zIndex: 1,
            fontSize: '20px'
          }} />
          <input
            type={showNewPassword ? "text" : "password"}
            maxLength={24}
            value={passwordFields.new}
            onChange={(e) => {
              setPasswordFields({
                ...passwordFields,
                new: e.target.value,
              });
              if (!passwordTouched.new) setPasswordTouched(prev => ({ ...prev, new: true }));
            }}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            style={{
              padding: '14px 16px',
              paddingLeft: '52px',
              paddingRight: '52px',
              width: '100%',
              boxSizing: 'border-box',
              border: newPasswordHasError ? '1px solid #e53e3e' : '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              fontSize: '15px',
              fontFamily: 'inherit',
              color: '#1e293b',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '16px',
              cursor: 'pointer',
              color: '#666',
              zIndex: 1,
              fontSize: '20px',
              transition: 'color 0.2s ease'
            }}
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            {showNewPassword ? <FiEye /> : <FiEyeOff />}
          </span>
        </div>

        {showNewPasswordErrors && !errorValidations.noSpaces && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            No spaces allowed
          </div>
        )}
        {showNewPasswordErrors && !errorValidations.noCommon && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            This is a common password
          </div>
        )}
        {showNewPasswordErrors && !errorValidations.noRepeats && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            No more than 2 repeated characters in a row
          </div>
        )}
        {showNewPasswordErrors && !errorValidations.notUsername && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            Password must not match your name
          </div>
        )}
        {showNewPasswordErrors && passwordFields.current.length > 0 && passwordFields.new === passwordFields.current && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            New password must be different from current password
          </div>
        )}

        <div style={{ marginTop: '12px' }}>
          <div style={{
            width: '100%',
            height: '6px',
            background: '#e0e0e0',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '6px'
          }}>
            <span style={{ 
              display: 'block', 
              height: '100%', 
              background: 'linear-gradient(to right, #3182ce, #4299e1, #63b3ed, #90cdf4)',
              transition: 'width 0.3s ease',
              width: `${(passwordStrength / 4) * 100}%` 
            }} />
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.4'
          }}>
            Use 8 or more characters with a mix of letters, numbers & symbols.
          </div>
        </div>
      </div>

      <div className="psp-field">
        <label>Confirm New Password</label>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          <FiLock style={{
            position: 'absolute',
            left: '16px',
            color: '#666',
            zIndex: 1,
            fontSize: '20px'
          }} />
          <input
            type={showConfirmPassword ? "text" : "password"}
            maxLength={24}
            value={passwordFields.confirm}
            onChange={(e) => {
              setPasswordFields({
                ...passwordFields,
                confirm: e.target.value,
              });
              if (!passwordTouched.confirm) setPasswordTouched(prev => ({ ...prev, confirm: true }));
            }}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            style={{
              padding: '14px 16px',
              paddingLeft: '52px',
              paddingRight: '52px',
              width: '100%',
              boxSizing: 'border-box',
              border: confirmPasswordHasError ? '1px solid #e53e3e' : '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              fontSize: '15px',
              fontFamily: 'inherit',
              color: '#1e293b',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '16px',
              cursor: 'pointer',
              color: '#666',
              zIndex: 1,
              fontSize: '20px',
              transition: 'color 0.2s ease'
            }}
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
          </span>
        </div>

        {showConfirmPasswordErrors && passwordFields.new.length > 0 && passwordFields.confirm !== passwordFields.new && (
          <div style={{
            color: '#e53e3e',
            fontSize: '13px',
            marginTop: '6px',
            lineHeight: '1.4'
          }}>
            Passwords must match
          </div>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid rgba(255,255,255,0.3)'
      }}>
        {!isMandatory && (
          <button
            style={{
              padding: '10px 20px',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#475569',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
            onClick={handlePasswordCancel}
            disabled={isPasswordSubmitting}
          >
            Cancel
          </button>
        )}

        <button
          type="button"
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: (!allValid || isPasswordSubmitting) ? 'not-allowed' : 'pointer',
            boxShadow: '0 8px 24px rgba(59,130,246,0.3), 0 4px 12px rgba(59,130,246,0.2)',
            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
            opacity: (!allValid || isPasswordSubmitting) ? 0.5 : 1,
          }}
          disabled={!allValid || isPasswordSubmitting}
          onClick={handlePasswordUpdate}
        >
          {isPasswordSubmitting ? "Updating..." : "Update Changes"}
        </button>
      </div>
    </>
  );
};

export default UserChangePassword;
